import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Device, RepairOption } from '../types';

export interface CartItem {
  id: string;
  deviceId: string;
  repairId: string;
  title: string;
  price: number;
  quantity: number;
  isExpress: boolean;
  device: Device;
  repair: RepairOption;
}

interface ShippingInfo {
  subtotal: number;
  shippingCost: number;
  totalAmount: number;
  freeShippingThreshold: number;
  isFreeShipping: boolean;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  isCartOpen: boolean;
  total: number;
  shipping: ShippingInfo | null;
  isLoadingShipping: boolean;
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  closeCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
  calculateShipping: () => Promise<void>;
  getSubtotal: () => number;
  getShippingCost: () => number;
  getGrandTotal: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      shipping: null,
      isLoadingShipping: false,
      get isCartOpen() { return get().isOpen },
      get total() { return get().getTotalPrice() },

      addItem: (newItem) => {
        const items = get().items;
        const existingItem = items.find(item =>
          item.deviceId === newItem.deviceId &&
          item.repairId === newItem.repairId &&
          item.isExpress === newItem.isExpress
        );

        if (existingItem) {
          // Add the new quantity to existing quantity
          set({
            items: items.map(item =>
              item.id === existingItem.id
                ? { ...item, quantity: item.quantity + newItem.quantity }
                : item
            )
          });
        } else {
          // Create new item with specified quantity
          set({
            items: [...items, { ...newItem, id: Math.random().toString(36).substr(2, 9) }]
          });
        }

        // Debug: Log what's being stored
        console.log('Cart updated, items:', get().items);
        console.log('New item added with quantity:', newItem.quantity);

        // Recalculate shipping when items change
        setTimeout(() => get().calculateShipping(), 100);
      },

      removeItem: (id) => {
        set({ items: get().items.filter(item => item.id !== id) });
        // Recalculate shipping when items change
        setTimeout(() => get().calculateShipping(), 100);
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        
        set({
          items: get().items.map(item =>
            item.id === id ? { ...item, quantity } : item
          )
        });
        // Recalculate shipping when items change
        setTimeout(() => get().calculateShipping(), 100);
      },

      clearCart: () => {
        set({ items: [], shipping: null });
      },

      toggleCart: () => {
        set({ isOpen: !get().isOpen });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + (item.price * item.quantity), 0);
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      calculateShipping: async () => {
        const items = get().items;
        if (items.length === 0) {
          set({ shipping: null });
          return;
        }

        set({ isLoadingShipping: true });
        
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
          const response = await fetch(`${apiUrl}/orders/calculate-shipping`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: items.map(item => ({
                price: item.price,
                quantity: item.quantity
              }))
            })
          });

          if (response.ok) {
            const data = await response.json();
            set({ shipping: data.data });
          } else {
            console.error('Failed to calculate shipping');
            set({ shipping: null });
          }
        } catch (error) {
          console.error('Error calculating shipping:', error);
          // Fallback shipping calculation when backend is unavailable
          const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const shippingCost = subtotal >= 50 ? 0 : 4.99; // Free shipping over 50€
          const totalAmount = subtotal + shippingCost;

          set({
            shipping: {
              subtotal,
              shippingCost,
              totalAmount,
              freeShippingThreshold: 50,
              isFreeShipping: shippingCost === 0
            }
          });
        } finally {
          set({ isLoadingShipping: false });
        }
      },

      getSubtotal: () => {
        return get().shipping?.subtotal ?? get().getTotalPrice();
      },

      getShippingCost: () => {
        return get().shipping?.shippingCost ?? 0;
      },

      getGrandTotal: () => {
        return get().shipping?.totalAmount ?? get().getTotalPrice();
      }
    }),
    {
      name: 'phonemedic-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        items: state.items,
        shipping: state.shipping
      })
    }
  )
);